interface MeetingRoomData {
  availability?: { start: string; end: string }[];
  busyEvents?: any[];
  calenderConnected?: boolean;
  isZeroEvents?: boolean; // Indicates if calendar events fetched resulted in zero events
  cachedAt?: number;
}

/**
 * Process changes in meeting room list, handling additions, removals, and caching
 * @param currentWeekRange - Current week range identifier
 * @param meetingRoomsData - Current meeting rooms data
 * @param meetingRoomIds - List of meeting rooms IDs to process
 * @param tempCacheRef - Ref for caching removed meeting room data
 * @param timeoutIdsRef - Ref for timeout IDs
 * @param inProgressMeetingRoomsRef - Ref tracking in-progress meeting rooms
 * @param clearCache - Function to clear cache entries
 * @param setMeetingRoomsData - Function to update meetingRoomsData state
 * @param CACHE_TIMEOUT - Cache timeout in milliseconds
 * @returns Object containing meeting rooms to fetch
 */
export function schProcessMeetingRoomChanges({
  currentWeekRange,
  meetingRoomsData,
  meetingRoomIds,
  tempCacheRef,
  timeoutIdsRef,
  inProgressMeetingRoomsRef,
  clearCache,
  setMeetingRoomsData,
  CACHE_TIMEOUT,
}: {
  currentWeekRange: string;
  meetingRoomsData: Record<string, Record<string, MeetingRoomData>>;
  meetingRoomIds: (string | number)[];
  tempCacheRef: React.MutableRefObject<
    Record<string, Record<string, MeetingRoomData>>
  >;
  timeoutIdsRef: React.MutableRefObject<Record<string, NodeJS.Timeout>>;
  inProgressMeetingRoomsRef: React.MutableRefObject<Set<string | number>>;
  clearCache: (key?: string) => void;
  setMeetingRoomsData: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, MeetingRoomData>>>
  >;
  CACHE_TIMEOUT: number;
}) {
  // If no current week range, fetch all meeting rooms
  if (!currentWeekRange) {
    return { toFetch: meetingRoomIds };
  }

  // Get current meeting rooms from state, converting IDs to match input type
  const currentMeetingRoomsInData = meetingRoomsData[currentWeekRange]
    ? Object.keys(meetingRoomsData[currentWeekRange]).map((id) =>
        typeof meetingRoomIds[0] === 'number' ? Number(id) : id
      )
    : [];

  // Handle removed meeting rooms
  const removedMeetingRooms = currentMeetingRoomsInData.filter(
    (id) => !meetingRoomIds.includes(id)
  );
  if (removedMeetingRooms.length > 0) {
    const updatedData = { ...meetingRoomsData };
    if (!tempCacheRef.current[currentWeekRange]) {
      tempCacheRef.current[currentWeekRange] = {};
    }

    removedMeetingRooms.forEach((id) => {
      if (updatedData[currentWeekRange][id]) {
        const cacheKey = `${currentWeekRange}_${id}`;
        tempCacheRef.current[currentWeekRange][id] = {
          ...updatedData[currentWeekRange][id],
          cachedAt: Date.now(),
        };
        delete updatedData[currentWeekRange][id];

        if (timeoutIdsRef.current[cacheKey]) {
          clearTimeout(timeoutIdsRef.current[cacheKey]);
        }
        timeoutIdsRef.current[cacheKey] = setTimeout(() => {
          clearCache(cacheKey);
        }, CACHE_TIMEOUT);

        inProgressMeetingRoomsRef.current.delete(id);
      }
    });

    setMeetingRoomsData(updatedData);
  }

  // Determine meeting rooms to fetch or restore
  const toRestore: (string | number)[] = [];
  const toFetch: (string | number)[] = [];

  meetingRoomIds.forEach((id) => {
    const isInData = meetingRoomsData[currentWeekRange]?.[id];
    // Check if data exists and is fully processed (not in progress)
    if (isInData && !inProgressMeetingRoomsRef.current.has(id)) {
      return;
    }

    const cachedData = tempCacheRef.current[currentWeekRange]?.[id];
    if (cachedData && Date.now() - cachedData.cachedAt! < CACHE_TIMEOUT) {
      toRestore.push(id);
    } else {
      toFetch.push(id); // Fetch if no data or in progress
    }
  });

  if (toRestore.length > 0) {
    const updatedData = {
      ...meetingRoomsData,
      [currentWeekRange]: { ...(meetingRoomsData[currentWeekRange] || {}) },
    };

    toRestore.forEach((id) => {
      updatedData[currentWeekRange][id] = {
        ...tempCacheRef.current[currentWeekRange][id],
      };
      const cacheKey = `${currentWeekRange}_${id}`;
      clearCache(cacheKey);
      inProgressMeetingRoomsRef.current.delete(id);
    });

    setMeetingRoomsData(updatedData);
  }

  // Combine with in-progress meeting rooms, deduplicate, and filter
  const combinedIds = toFetch.slice();
  inProgressMeetingRoomsRef.current.forEach((id: string | number) => {
    if (!combinedIds.includes(id)) {
      combinedIds.push(id);
    }
  });
  const allToFetch = combinedIds.filter(
    (id) =>
      !currentMeetingRoomsInData.includes(id) ||
      inProgressMeetingRoomsRef.current.has(id)
  );

  return { toFetch: allToFetch };
}
